import React, { useCallback, useContext, useMemo, useState } from 'react';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDAvenyProgress from '../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { useMutation } from '@apollo/client';
import GravTitelKort from '../GravTitelKort';
import FotoKort from '../FotoKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { getAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import AttributeDisplayText from '../../../../../../Components/Attributes/AttributeDisplayText/AttributeDisplayText';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDDivider from '../../../../../../BaseComponents/Divider/EDDivider';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { formatDecimal } from '../Utils';
import { BusinessDataFormContext } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { getMaskedFromCurrentValues } from '../../../../_Common/Hooks/useAttributeValues';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import GravplatsUpdateMutation from '../Mutations/GravplatsUpdateMutation.gql';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

const currentYear = new Date().getFullYear().toString();

const GravModel: IClientBusinessModel =
  clientAvenyModel.GA.BusinessModels.GAGrav;

interface IOversiktProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

export const Oversikt: React.FC<IOversiktProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const { avenyCollectionMap } = useContext(ModelContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const formContext = useContext(BusinessDataFormContext);
  const [gravplatsUpdateMutation, { loading: kanneteckenSaving }] = useMutation(
    GravplatsUpdateMutation
  );
  const [kanneteckenDialogOpen, setKanneteckenDialogOpen] = useState(false);
  const [editingKannetecken, setEditingKannetecken] = useState('');
  const [kanneteckenError, setKanneteckenError] = useState<string | null>(null);

  const anteckningar = nodeData?.ANTECKNINGAR ?? '';

  // Attribute info for Gravplatsdetaljer card
  const attrInfoGravTyp = getAttributeInfo(
    GravModel,
    'GravTyp',
    avenyCollectionMap
  );
  const attrInfoStatusBen = getAttributeInfo(
    GravModel,
    'StatusBen',
    avenyCollectionMap
  );
  const attrInfoGravratt = getAttributeInfo(
    GravModel,
    'Gravratt',
    avenyCollectionMap
  );
  const attrInfoStorlek = getAttributeInfo(
    GravModel,
    'Storlek',
    avenyCollectionMap
  );
  const attrInfoKannetecken = getAttributeInfo(
    GravModel,
    'KANNETECKEN',
    avenyCollectionMap
  );
  const attrInfoKultur = getAttributeInfo(
    GravModel,
    'AnordningKultur.BENAMNING',
    avenyCollectionMap
  );

  // Foto
  const attrInfoBild = getAttributeInfo(
    GravModel,
    'Blob.BILD',
    avenyCollectionMap
  );
  const attrInfoBildAndrad = getAttributeInfo(
    GravModel,
    'Blob.BildAndrad',
    avenyCollectionMap
  );

  // Snabbinformation
  const attrInfoSkotselKategori = getAttributeInfo(
    GravModel,
    'SkotselKategori.BENAMNING',
    avenyCollectionMap
  );
  const attrInfoGravrInhOmbud = getAttributeInfo(
    GravModel,
    'GravrInhOmbud.IDString',
    avenyCollectionMap
  );

  // Tidslinje och status
  const attrInfoBesiktningsdatum = getAttributeInfo(
    GravModel,
    'Besiktningsdatum',
    avenyCollectionMap
  );
  const attrInfoGravrattTi = getAttributeInfo(
    GravModel,
    'GRAVRATT_TI',
    avenyCollectionMap
  );
  const attrInfoAndringsdatum = getAttributeInfo(
    GravModel,
    'ANDRINGSDATUM',
    avenyCollectionMap
  );

  // Senast gravsatta
  const attrInfoNamnFE = getAttributeInfo(
    GravModel,
    'Gravsatta.Avlidna.Person.NamnFE',
    avenyCollectionMap
  );
  const attrInfoPersNr = getAttributeInfo(
    GravModel,
    'Gravsatta.Avlidna.Person.PERS_NR',
    avenyCollectionMap
  );
  const attrInfoAvlidenDatum = getAttributeInfo(
    GravModel,
    'Gravsatta.Avlidna.AVLIDEN_DATUM',
    avenyCollectionMap
  );
  const attrInfoGravsDatum = getAttributeInfo(
    GravModel,
    'Gravsatta.Avlidna.GravsDatum',
    avenyCollectionMap
  );

  // Snabbinfo
  const attrInfoGravBokad = getAttributeInfo(
    GravModel,
    'BOKAD',
    avenyCollectionMap
  );

  const currentKannetecken = useMemo(
    () =>
      formContext?.formState.currentValues?.KANNETECKEN ??
      nodeData?.KANNETECKEN ??
      '',
    [formContext?.formState.currentValues?.KANNETECKEN, nodeData?.KANNETECKEN]
  );

  const maskedKannetecken = getMaskedFromCurrentValues(
    formContext?.formState.currentValues,
    'KANNETECKEN',
    attrInfoKannetecken.attribute
  );

  const handleOpenKanneteckenDialog = useCallback(() => {
    setEditingKannetecken(currentKannetecken);
    setKanneteckenDialogOpen(true);
  }, [currentKannetecken]);

  const handleCloseKanneteckenDialog = useCallback(() => {
    setKanneteckenDialogOpen(false);
    setKanneteckenError(null);
  }, []);

  const handleSaveKannetecken = useCallback(async () => {
    if (
      !formContext ||
      !nodeData?.id ||
      nodeData._version == null ||
      !activeDatabase
    ) {
      return;
    }

    setKanneteckenError(null);

    try {
      formContext.update([
        {
          operation: 'OwnFields',
          values: { KANNETECKEN: editingKannetecken },
        },
      ]);

      await gravplatsUpdateMutation({
        variables: {
          db: activeDatabase,
          skotselAr: currentYear,
          values: {
            id: nodeData.id,
            _version: nodeData._version,
            KANNETECKEN: editingKannetecken,
          },
        },
      });

      await formContext.refetchData?.();

      setKanneteckenDialogOpen(false);
    } catch (err: any) {
      setKanneteckenError(
        err?.message || 'Ett fel uppstod vid sparande av kännetecken.'
      );
    }
  }, [
    activeDatabase,
    editingKannetecken,
    formContext,
    gravplatsUpdateMutation,
    nodeData?.id,
    nodeData?._version,
  ]);

  const isKanneteckenUnchanged = editingKannetecken === currentKannetecken;

  const isPageLoading = !nodeData;

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Översikt'}
      collectionName={GravModel.CollectionName}
    >
      <EDBox sx={{ position: 'relative' }}>
        {isPageLoading && <EDAvenyProgress overlay />}
        <ObjektVyKolumner>
          <ObjektVyKolumn fullPage={fullPage}>
            <GravTitelKort nodeData={nodeData} />
            {isSmallScreen && (
              <FotoKort
                nodeData={nodeData}
                attrInfoBildAndrad={attrInfoBildAndrad}
                attrInfoBild={attrInfoBild}
              />
            )}
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Gravplatsdetaljer"
              button={() => (
                <EDButton
                  flat
                  label={isSmallScreen ? undefined : 'REDIGERA'}
                  tooltip="Redigera kännetecken"
                  size="small"
                  disabled={maskedKannetecken || kanneteckenSaving}
                  onClick={handleOpenKanneteckenDialog}
                  icon={
                    <EDIcon
                      iconType="edit"
                      marginRight={0.5}
                      color="primary"
                      size="small"
                    />
                  }
                />
              )}
            >
              <EDCaptionedValue
                label="Kännetecken"
                name={attrInfoKannetecken.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={currentKannetecken}
                  attributeModel={attrInfoKannetecken.attribute}
                  collection={GravModel}
                  forceText
                  masked={maskedKannetecken}
                />
              </EDCaptionedValue>
              <EDCaptionedValue
                label="Typ"
                name={attrInfoGravTyp.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.GravTyp}
                  attributeModel={attrInfoGravTyp.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'GravTyp',
                    attrInfoGravTyp.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Status"
                name={attrInfoStatusBen.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.StatusBen}
                  attributeModel={attrInfoStatusBen.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'StatusBen',
                    attrInfoStatusBen.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Upplåtelsetid"
                name={attrInfoGravratt.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.Gravratt}
                  attributeModel={attrInfoGravratt.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'Gravratt',
                    attrInfoGravratt.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Storlek"
                name={attrInfoStorlek.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.Storlek}
                  attributeModel={attrInfoStorlek.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'Storlek',
                    attrInfoStorlek.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Kultur/bevarandevärde"
                name={attrInfoKultur.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?._Associations?.AnordningKultur?.BENAMNING}
                  attributeModel={attrInfoKultur.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'AnordningKultur.BENAMNING',
                    attrInfoKultur.attribute
                  )}
                />
              </EDCaptionedValue>
            </ObjektVyKort>
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Gravsatta mallar"
            >
              <EDCaptionedValue label="Längd" name="SLEK_L" size="large">
                <EDTypography>{formatDecimal(nodeData?.SLEK_L)}</EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Bredd" name="SLEK_B" size="large">
                <EDTypography>{formatDecimal(nodeData?.SLEK_B)}</EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Yta" name="Yta" size="large">
                <EDTypography>{formatDecimal(nodeData?.Yta)}</EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Kod" name="Mall.KOD" size="large">
                <EDTypography>
                  {nodeData?._Associations?.Mall?.KOD ?? '-'}
                </EDTypography>
              </EDCaptionedValue>
            </ObjektVyKort>

            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Senast gravsatta"
            >
              {nodeData?._Associations?.Gravsatta_All?.length &&
              nodeData._Associations.Gravsatta_All.length > 0 ? (
                nodeData._Associations.Gravsatta_All.map((item, index) => (
                  <div
                    key={item?.id ?? index}
                    style={{
                      marginBottom:
                        index <
                        (nodeData?._Associations?.Gravsatta_All?.length ?? 0) -
                          1
                          ? 20
                          : 0,
                    }}
                  >
                    <EDCaptionedValue
                      label="Namn"
                      name={`${attrInfoNamnFE.attribute.AttributeName}_${index}`}
                      size="large"
                    >
                      <AttributeDisplayText
                        value={
                          item?._Associations?.Avlidna?._Associations?.Person
                            ?.NamnFE
                        }
                        attributeModel={attrInfoNamnFE.attribute}
                        collection={GravModel}
                        forceText
                        masked={getMaskedFromCurrentValues(
                          item,
                          `Avlidna.Person.NamnFE`,
                          attrInfoNamnFE.attribute
                        )}
                      />
                    </EDCaptionedValue>

                    <EDCaptionedValue
                      label="Personnummer"
                      name={`${attrInfoPersNr.attribute.AttributeName}_${index}`}
                      size="large"
                    >
                      <AttributeDisplayText
                        value={
                          item?._Associations?.Avlidna?._Associations?.Person
                            ?.PERS_NR
                        }
                        attributeModel={attrInfoPersNr.attribute}
                        collection={GravModel}
                        forceText
                        masked={getMaskedFromCurrentValues(
                          item,
                          'Avlidna.Person.PERS_NR',
                          attrInfoPersNr.attribute
                        )}
                      />
                    </EDCaptionedValue>

                    <EDCaptionedValue
                      label="Dödsdatum"
                      name={`${attrInfoAvlidenDatum.attribute.AttributeName}_${index}`}
                      size="large"
                    >
                      <AttributeDisplayText
                        value={item?._Associations?.Avlidna?.AVLIDEN_DATUM}
                        attributeModel={attrInfoAvlidenDatum.attribute}
                        collection={GravModel}
                        forceText
                        masked={getMaskedFromCurrentValues(
                          item,
                          'Avlidna.AVLIDEN_DATUM',
                          attrInfoAvlidenDatum.attribute
                        )}
                      />
                    </EDCaptionedValue>

                    <EDCaptionedValue
                      label="Gravsatt"
                      name={`${attrInfoGravsDatum.attribute.AttributeName}_${index}`}
                      size="large"
                    >
                      <AttributeDisplayText
                        value={item?._Associations?.Avlidna?.GravsDatum}
                        attributeModel={attrInfoGravsDatum.attribute}
                        collection={GravModel}
                        forceText
                        masked={getMaskedFromCurrentValues(
                          item,
                          'Avlidna.GravsDatum',
                          attrInfoGravsDatum.attribute
                        )}
                      />
                    </EDCaptionedValue>

                    <EDCaptionedValue
                      label="Typ"
                      name={`typ_${index}`}
                      size="large"
                    >
                      <EDTypography>
                        {[
                          item?._Associations?.Avlidna?.GRAVTYP,
                          item?.DJUP,
                          item?.Placering,
                        ]
                          .filter(Boolean)
                          .join(' ') || '-'}
                      </EDTypography>
                    </EDCaptionedValue>
                    {index <
                      (nodeData?._Associations?.Gravsatta_All?.length ?? 0) -
                        1 && <EDDivider paddingTop={2} />}
                  </div>
                ))
              ) : (
                <EDTypography>Inga gravsatta registrerade</EDTypography>
              )}
            </ObjektVyKort>
          </ObjektVyKolumn>
          <ObjektVyKolumn fullPage={fullPage}>
            {!isSmallScreen && (
              <FotoKort
                nodeData={nodeData}
                attrInfoBildAndrad={attrInfoBildAndrad}
                attrInfoBild={attrInfoBild}
              />
            )}
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Snabbinformation"
            >
              <EDCaptionedValue
                label="Skötsel"
                name={attrInfoSkotselKategori.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?._Associations?.SkotselKategori?.BENAMNING}
                  attributeModel={attrInfoSkotselKategori.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'SkotselKategori.BENAMNING',
                    attrInfoSkotselKategori.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Gravrättsinnehavare"
                name={attrInfoGravrInhOmbud.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?._Associations?.GravrInhOmbud?.IDString}
                  attributeModel={attrInfoGravrInhOmbud.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'GravrInhOmbud.IDString',
                    attrInfoGravrInhOmbud.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Anordningar"
                name="anordningar"
                size="large"
              >
                <EDTypography>
                  {[
                    nodeData?._Associations?.Anordningar_All?.[0]?.FullIDString,
                    nodeData?._Associations?.Anordningar_All?.[0]?.ANBENAMNING,
                    nodeData?._Associations?.Anordningar_All?.[0]
                      ?.SenasteStenStatus,
                  ]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Händelser" name="handelser" size="large">
                <EDTypography>
                  {nodeData?._Associations?.Handelser_All?.length
                    ? `${nodeData._Associations.Handelser_All.length} st`
                    : '—'}
                </EDTypography>
              </EDCaptionedValue>
              {nodeData?.BOKAD && (
                <EDCaptionedValue
                  label="Bokad gravplats"
                  name={attrInfoGravBokad.attribute.AttributeName}
                  size="large"
                >
                  <AttributeDisplayText
                    value={nodeData?.BOKAD}
                    attributeModel={attrInfoGravBokad.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      formContext?.formState.currentValues,
                      'BOKAD',
                      attrInfoGravBokad.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}
            </ObjektVyKort>
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Tidslinje och status"
            >
              <EDCaptionedValue
                label="Senaste inventering"
                name={attrInfoBesiktningsdatum.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.Besiktningsdatum}
                  attributeModel={attrInfoBesiktningsdatum.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'Besiktningsdatum',
                    attrInfoBesiktningsdatum.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Upplåtelsen utgår"
                name={attrInfoGravrattTi.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.GRAVRATT_TI}
                  attributeModel={attrInfoGravrattTi.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'GRAVRATT_TI',
                    attrInfoGravrattTi.attribute
                  )}
                />
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Senast ändrad"
                name={attrInfoAndringsdatum.attribute.AttributeName}
                size="large"
              >
                <AttributeDisplayText
                  value={nodeData?.ANDRINGSDATUM}
                  attributeModel={attrInfoAndringsdatum.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    formContext?.formState.currentValues,
                    'ANDRINGSDATUM',
                    attrInfoAndringsdatum.attribute
                  )}
                />
              </EDCaptionedValue>
            </ObjektVyKort>
            {/* Anteckningar */}
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Anteckningar"
            >
              {anteckningar ? (
                <EDTypography sx={{ whiteSpace: 'pre-wrap' }}>
                  {anteckningar}
                </EDTypography>
              ) : (
                <EDTypography color="textSecondary">
                  Inga anteckningar registrerade
                </EDTypography>
              )}
            </ObjektVyKort>
          </ObjektVyKolumn>
        </ObjektVyKolumner>
      </EDBox>

      <EDDialog
        open={kanneteckenDialogOpen}
        onClose={handleCloseKanneteckenDialog}
        title="Redigera kännetecken"
        maxWidth="sm"
        fullWidth
        footerActions={[
          { label: 'Avbryt', onClick: handleCloseKanneteckenDialog },
          {
            label: 'Spara',
            onClick: handleSaveKannetecken,
            buttonProps: {
              disabled:
                kanneteckenSaving ||
                isKanneteckenUnchanged ||
                !activeDatabase ||
                !nodeData?.id ||
                nodeData._version == null,
            },
          },
        ]}
      >
        <EDTextEdit
          label="Kännetecken"
          value={editingKannetecken}
          onChange={setEditingKannetecken}
          multiline
          minRows={4}
          fullWidth
        />
        {kanneteckenError && (
          <EDTypography color="error" sx={{ mt: 2 }}>
            {kanneteckenError}
          </EDTypography>
        )}
      </EDDialog>
    </ObjektVySida>
  );
};

export default Oversikt;
